import React, { useState } from "react";
import { motion } from "framer-motion";
import { CurrencyDollar, TrendUp, Users, ShieldCheck } from "@phosphor-icons/react";
import { useFeeRates } from "@/hooks/useFeeRates";

/**
 * Simulador de ganhos ao vivo.
 * Slider R$ 50–5000 → cálculo do split produtor/afiliado/plataforma em tempo real
 * usando as taxas dinâmicas do banco (useFeeRates).
 */
const brl = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function EarningSimulator() {
  const [amount, setAmount] = useState(297);
  const [affiliate, setAffiliate] = useState(true);
  const { rates } = useFeeRates();

  const platformCut = amount * (rates.sales_commission_rate || 0.08);
  const affiliateRate = rates.default_affiliate_rate || 0.1;
  const affiliateCut = affiliate ? amount * affiliateRate : 0;
  const producerCut = amount - platformCut - affiliateCut;

  const pctPlat = ((platformCut / amount) * 100 || 0).toFixed(1);
  const pctAff = ((affiliateCut / amount) * 100 || 0).toFixed(1);
  const pctProd = ((producerCut / amount) * 100 || 0).toFixed(1);

  return (
    <div className="bento-card halo-accent" data-testid="earning-simulator">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/40 flex items-center justify-center">
          <TrendUp size={18} color="var(--accent)" weight="fill" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">
            Simulador ao vivo
          </div>
          <h3 className="text-fluid-h3 font-display font-black text-white mt-0.5">
            Faça as contas.
          </h3>
        </div>
      </div>

      {/* Slider */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <label className="text-xs text-white/60 font-mono">
            valor da venda
          </label>
          <div className="font-display font-black text-fluid-h3 text-[var(--accent)] tabular-nums" data-testid="sim-amount">
            {brl(amount)}
          </div>
        </div>
        <input
          type="range"
          min="47"
          max="5000"
          step="10"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="mod-range w-full"
          style={{ "--val": `${((amount - 47) / (5000 - 47)) * 100}%` }}
          aria-label="Valor da venda"
          data-testid="sim-slider"
        />
        <div className="flex justify-between text-[10px] text-white/40 font-mono mt-1">
          <span>R$ 47</span>
          <span>R$ 5.000</span>
        </div>
      </div>

      {/* Toggle afiliado */}
      <label className="flex items-center gap-3 mb-6 select-none cursor-pointer group">
        <div
          className={`w-10 h-6 rounded-full border transition-colors ${
            affiliate
              ? "bg-[var(--accent)]/30 border-[var(--accent)]"
              : "bg-white/5 border-white/10"
          } relative`}
          data-testid="sim-toggle-affiliate"
        >
          <div
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform ${
              affiliate ? "translate-x-4 bg-[var(--accent)]" : "bg-white/60"
            }`}
          />
        </div>
        <input
          type="checkbox"
          checked={affiliate}
          onChange={(e) => setAffiliate(e.target.checked)}
          className="sr-only"
        />
        <span className="text-sm text-white/80 group-hover:text-white transition-colors">
          venda gerada por afiliado ({(affiliateRate * 100).toFixed(0)}%)
        </span>
      </label>

      {/* Barras de split animadas */}
      <div className="space-y-3">
        <SplitBar
          label="Produtor recebe"
          value={producerCut}
          pct={pctProd}
          color="#10b981"
          Icon={ShieldCheck}
          testid="sim-producer"
        />
        {affiliate && (
          <SplitBar
            label="Afiliado ganha"
            value={affiliateCut}
            pct={pctAff}
            color="#60a5fa"
            Icon={Users}
            testid="sim-affiliate"
          />
        )}
        <SplitBar
          label="Plataforma retém"
          value={platformCut}
          pct={pctPlat}
          color="var(--accent)"
          Icon={CurrencyDollar}
          testid="sim-platform"
        />
      </div>

      <p className="text-[11px] text-white/40 mt-5 leading-relaxed">
        * split real aplicado via <span className="text-white/70 font-mono">application_fee</span> do Mercado Pago.
        Nenhum repasse manual. Cada plataforma ajusta suas taxas no admin.
      </p>
    </div>
  );
}

function SplitBar({ label, value, pct, color, Icon, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <div className="flex items-center gap-2 text-white/70">
          <Icon size={14} color={color} weight="fill" />
          <span>{label}</span>
        </div>
        <div className="font-display font-bold text-white tabular-nums">
          {brl(value)}
          <span className="text-white/40 ml-2 font-mono text-[10px]">
            {pct}%
          </span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
}
